// Globale Schnellerfassung (UX-Vorschlag P2, Flag `ux_web_p2`).
//
// Ein Feld für persönliche UND Projektaufgaben, überall erreichbar mit N oder
// über ⌘K → „Erfassen". Der vorhandene Parser (lib/quickAdd.ts, `extended`)
// erkennt heute/morgen/Datum, !priorität, @person, #Projekt, +Liste und
// Uhrzeit; die Vorschau zeigt Ziel, Fälligkeit, Priorität und Zuständige,
// BEVOR angelegt wird. Die Auflösung steckt testbar in lib/capture.ts.
//
// Tasten im Feld: ↵ anlegen · ⇧↵ anlegen & öffnen · ⌘↵/Strg↵ anlegen & nächste
// · ↹/⇧↹ Phase wechseln · ↑/↓ bei mehrdeutigem #Projekt bzw. +Liste wählen.
//
// Gemountet genau einmal in App.tsx; geöffnet nur, wenn uxEnabled("ux_web_p2").

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { useLocation } from "wouter-preact";
import type { Phase, TaskCreate, TaskPriority } from "@juuplan/shared";
import { Modal } from "./Modal";
import { parseQuickAdd } from "../lib/quickAdd";
import {
  cyclePhase,
  defaultPhase,
  formatDuePreview,
  isTargetReady,
  listAllowsAssignee,
  matchAssignee,
  reminderIso,
  resolveCaptureTarget,
  type CaptureTarget,
} from "../lib/capture";
import { localTodayIso, softPillStyle, TASK_PRIORITY_LABEL, TASK_PRIORITY_VAR } from "../lib/format";
import { api } from "../lib/api";
import { useProjects } from "../store/projects";
import { useLists, createPersonalTask } from "../store/personalTasks";
import { useUserPrefs } from "../store/userPrefs";
import { useUsers } from "../store/users";
import { createTask } from "../store/project";
import { createReminder } from "../store/reminders";
import { openPersonalTaskSig, openProjectTaskSig } from "../store/deepLinks";
import { captureInitial, captureMyDay, closeCapture, pushRecentCapture, recentCaptures } from "../store/capture";
import { addToMyDay } from "../store/myDay";

type SubmitMode = "close" | "open" | "next";

// Phasen je Projekt — einmal pro Sitzung geladen.
const phaseCache = new Map<number, Phase[]>();

function Kbd({ label }: { label: string }): preact.JSX.Element {
  return (
    <kbd class="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)] text-micro font-mono text-[color:var(--color-on-surface-variant)]">
      {label}
    </kbd>
  );
}

function Row({ label, children }: { label: string; children: preact.ComponentChildren }): preact.JSX.Element {
  return (
    <div class="grid grid-cols-[6.5rem_1fr] gap-3 py-1 text-sm">
      <dt class="text-[color:var(--color-on-surface-variant)]">{label}</dt>
      <dd class="min-w-0 text-[color:var(--color-on-surface)]">{children}</dd>
    </div>
  );
}

const muted = "text-[color:var(--color-on-surface-variant)]";

/** Die Aufgabe steht schon — eine abgelehnte Erinnerung (z. B. Zeit vorbei)
 *  darf das Anlegen nicht als gescheitert erscheinen lassen, sonst legt ein
 *  zweites ↵ die Aufgabe doppelt an. Der Fehler-Toast kommt aus api(). */
/** Wie tryReminder: die Aufgabe steht schon, ein Fehler beim Einplanen in
 *  „Mein Tag" darf kein zweites Anlegen provozieren (Toast kommt aus api()). */
async function tryMyDay(body: Parameters<typeof addToMyDay>[0]): Promise<void> {
  try {
    await addToMyDay(body);
  } catch {
    /* bereits getoastet */
  }
}

async function tryReminder(body: Parameters<typeof createReminder>[0]): Promise<void> {
  try {
    await createReminder(body);
  } catch {
    /* bereits getoastet */
  }
}

export function CaptureDialog(): preact.JSX.Element {
  const [value, setValue] = useState(() => (captureInitial.value ? `${captureInitial.value} ` : ""));
  const [busy, setBusy] = useState(false);
  // Beim Öffnen festgehalten (P1: „+ Aufgabe für heute" auf Mein Tag).
  const [myDay] = useState<string | null>(() => captureMyDay.value);
  const [pickedProjectId, setPickedProjectId] = useState<number | null>(null);
  const [pickedListId, setPickedListId] = useState<number | null>(null);
  const [phaseOverride, setPhaseOverride] = useState<Record<number, number>>({});
  const [phases, setPhases] = useState<{ projectId: number; items: Phase[] } | { projectId: number; error: true } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [, setLocation] = useLocation();

  const projects = useProjects();
  const { lists, loading: listsLoading } = useLists();
  const prefs = useUserPrefs();
  const users = useUsers();

  const today = localTodayIso();
  const parsed = useMemo(() => parseQuickAdd(value, new Date(), { extended: true }), [value]);

  const target: CaptureTarget = resolveCaptureTarget({
    projectQuery: parsed.projectQuery,
    listQuery: parsed.listQuery,
    projects: projects.items,
    lists,
    defaultListId: prefs.default_list_id,
    pickedProjectId,
    pickedListId,
  });

  const projectId = target.kind === "project" ? target.project.id : null;

  // Phasen des erkannten Projekts laden. GET-Fehler toasten in api() NICHT —
  // deshalb merken wir den Fehler und zeigen ihn in der Vorschau an.
  useEffect(() => {
    if (projectId === null) return;
    const cached = phaseCache.get(projectId);
    if (cached) {
      setPhases({ projectId, items: cached });
      return;
    }
    let alive = true;
    setPhases(null);
    api<Phase[]>(`/api/projects/${projectId}/phases`)
      .then((items) => {
        phaseCache.set(projectId, items);
        if (alive) setPhases({ projectId, items });
      })
      .catch(() => {
        if (alive) setPhases({ projectId, error: true });
      });
    return () => {
      alive = false;
    };
  }, [projectId]);

  // Fokus ins Feld — nach dem Modal, das beim Mounten sein Panel fokussiert.
  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, []);

  const phaseList = phases && phases.projectId === projectId && "items" in phases ? phases.items : null;
  const phaseError = phases !== null && phases.projectId === projectId && "error" in phases;
  const phase: Phase | null = (() => {
    if (!phaseList || projectId === null) return null;
    const overrideId = phaseOverride[projectId];
    return phaseList.find((p) => p.id === overrideId) ?? defaultPhase(phaseList);
  })();

  // Zuständige
  const assigneeMatches = parsed.assigneeName ? matchAssignee(users.items, parsed.assigneeName) : [];
  const assignee = assigneeMatches.length === 1 ? assigneeMatches[0]! : null;
  const assigneeAllowed = target.kind === "project" || (target.kind === "list" && listAllowsAssignee(target.list));

  const ready = parsed.title.trim().length > 0 && isTargetReady(target, phase) && !busy;
  const dueLabel = formatDuePreview(parsed.dueDate, parsed.time, today);
  const effectiveDue = parsed.dueDate ?? (parsed.time ? today : undefined);

  const cycleCandidates = (dir: 1 | -1): boolean => {
    if (target.kind === "project-ambiguous") {
      const c = target.candidates;
      const i = c.findIndex((p) => p.id === pickedProjectId);
      setPickedProjectId(c[(i + dir + c.length) % c.length]!.id);
      return true;
    }
    if (target.kind === "list-ambiguous") {
      const c = target.candidates;
      const i = c.findIndex((l) => l.id === pickedListId);
      setPickedListId(c[(i + dir + c.length) % c.length]!.id);
      return true;
    }
    return false;
  };

  const submit = async (mode: SubmitMode): Promise<void> => {
    if (!ready) return;
    const title = parsed.title.trim();
    setBusy(true);
    try {
      const remindAt = reminderIso(parsed.dueDate, parsed.time, today);
      if (target.kind === "project" && phase) {
        const body: TaskCreate = { title };
        if (parsed.priority) body.priority = parsed.priority;
        if (effectiveDue) body.end_date = effectiveDue;
        if (assignee) body.assignee_ids = [assignee.id];
        const task = await createTask(phase.id, body);
        if (remindAt) await tryReminder({ task_id: task.id, remind_at: remindAt });
        if (myDay) await tryMyDay({ task_id: task.id, day: myDay });
        const pid = target.project.id;
        const openIt = (): void => {
          openProjectTaskSig.value = task.id;
          setLocation(`/p/${pid}`);
          closeCapture();
        };
        pushRecentCapture({
          key: `t${task.id}`,
          title,
          where: `${target.project.name} · ${phase.name}`,
          due: dueLabel,
          open: openIt,
        });
        if (mode === "open") openIt();
      } else if (target.kind === "list") {
        const task = await createPersonalTask({
          title,
          list_id: target.list.id,
          ...(effectiveDue && { due_date: effectiveDue }),
          ...(parsed.priority && { priority: parsed.priority }),
          ...(assignee && assigneeAllowed && { assignee_ids: [assignee.id] }),
        });
        if (remindAt) await tryReminder({ personal_task_id: task.id, remind_at: remindAt });
        if (myDay) await tryMyDay({ personal_task_id: task.id, day: myDay });
        const openIt = (): void => {
          openPersonalTaskSig.value = task.id;
          setLocation(`/lists`);
          closeCapture();
        };
        pushRecentCapture({ key: `p${task.id}`, title, where: target.list.name, due: dueLabel, open: openIt });
        if (mode === "open") openIt();
      }
      setValue("");
      setPickedProjectId(null);
      setPickedListId(null);
      if (mode === "close") closeCapture();
    } catch {
      // Mutationen toasten ihre Fehler selbst (api()); die Eingabe bleibt stehen.
    } finally {
      setBusy(false);
      if (mode === "next") inputRef.current?.focus();
    }
  };

  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === "Enter") {
      e.preventDefault();
      const mode: SubmitMode = e.metaKey || e.ctrlKey ? "next" : e.shiftKey ? "open" : "close";
      void submit(mode);
      return;
    }
    if (e.key === "Tab" && target.kind === "project" && phaseList && phaseList.length > 1) {
      e.preventDefault();
      e.stopPropagation(); // nicht in die Fokusfalle des Modals laufen lassen
      const next = cyclePhase(phaseList, phase?.id ?? null, e.shiftKey ? -1 : 1);
      if (next) setPhaseOverride({ ...phaseOverride, [target.project.id]: next.id });
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      if (cycleCandidates(e.key === "ArrowDown" ? 1 : -1)) e.preventDefault();
    }
  };

  // ---- Vorschau: Ziel ----
  const targetView = ((): preact.JSX.Element => {
    switch (target.kind) {
      case "project":
        return (
          <span>
            <strong>{target.project.name}</strong>
            {" › "}
            {phaseError ? (
              <span class="text-[color:var(--color-error)]">Phasen konnten nicht geladen werden</span>
            ) : !phaseList ? (
              <span class={muted}>Phasen werden geladen …</span>
            ) : phase ? (
              <>
                Phase <strong>{phase.name}</strong>{" "}
                <span class={muted}>
                  ({phaseOverride[target.project.id] ? "gewählt" : "aktuelle Phase"}
                  {phaseList.length > 1 ? ", ↹ ändern" : ""})
                </span>
              </>
            ) : (
              <span class="text-[color:var(--color-error)]">Projekt hat keine Phase</span>
            )}
          </span>
        );
      case "project-ambiguous":
      case "list-ambiguous": {
        const isProject = target.kind === "project-ambiguous";
        const items = target.candidates as Array<{ id: number; name: string }>;
        const pickedId = isProject ? pickedProjectId : pickedListId;
        return (
          <div>
            <span class="text-[color:var(--color-error)]">
              {isProject ? "#" : "+"}
              {target.query} passt auf {items.length} — bitte wählen (↑/↓ oder klicken):
            </span>
            <div class="mt-1 flex flex-wrap gap-1" role="listbox" aria-label="Mögliche Ziele">
              {items.map((it) => (
                <button
                  key={it.id}
                  type="button"
                  role="option"
                  aria-selected={it.id === pickedId}
                  onClick={() => {
                    if (isProject) setPickedProjectId(it.id);
                    else setPickedListId(it.id);
                    inputRef.current?.focus();
                  }}
                  class="rounded border border-[color:var(--color-outline-variant)] px-2 py-0.5 text-xs hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                >
                  {it.name}
                </button>
              ))}
            </div>
          </div>
        );
      }
      case "project-none":
        return (
          <span class="text-[color:var(--color-error)]">
            {projects.error
              ? "Projekte konnten nicht geladen werden"
              : projects.loading
                ? "Projekte werden geladen …"
                : `Kein Projekt „${target.query}" gefunden, in dem du Aufgaben anlegen darfst`}
          </span>
        );
      case "list-none":
        return (
          <span class="text-[color:var(--color-error)]">
            {listsLoading ? "Listen werden geladen …" : `Keine Liste „${target.query}" gefunden`}
          </span>
        );
      case "list":
        return (
          <span>
            <strong>{target.list.name}</strong>{" "}
            <span class={muted}>{target.isDefault ? "(persönliche Standardliste)" : "(Liste)"}</span>
          </span>
        );
      case "no-list":
        return (
          <span class="text-[color:var(--color-error)]">
            {listsLoading ? "Listen werden geladen …" : "Keine Liste vorhanden, in die die Aufgabe kann"}
          </span>
        );
    }
  })();

  const assigneeView = !parsed.assigneeName ? (
    <span class={muted}>—</span>
  ) : assigneeMatches.length === 0 ? (
    <span class="text-[color:var(--color-error)]">„{parsed.assigneeName}" nicht gefunden — wird nicht gesetzt</span>
  ) : !assignee ? (
    <span class="text-[color:var(--color-error)]">
      @{parsed.assigneeName} ist mehrdeutig ({assigneeMatches.slice(0, 3).map((u) => u.name).join(", ")}
      {assigneeMatches.length > 3 ? " …" : ""}) — wird nicht gesetzt
    </span>
  ) : !assigneeAllowed ? (
    <span>
      {assignee.name} <span class={muted}>(nur auf geteilten Listen möglich — wird nicht gesetzt)</span>
    </span>
  ) : (
    <span>{assignee.name}</span>
  );

  const recent = recentCaptures.value;

  return (
    <Modal title="Erfassen" onClose={closeCapture} size="lg">
      <div class="space-y-4" data-testid="capture-dialog">
        <div class="flex items-center gap-2">
          <span class="text-[color:var(--color-primary)] text-lg select-none" aria-hidden="true">+</span>
          <input
            ref={inputRef}
            type="text"
            value={value}
            onInput={(e) => setValue((e.target as HTMLInputElement).value)}
            onKeyDown={onKeyDown}
            disabled={busy}
            maxLength={300}
            placeholder="z. B. Rückruf Kunde morgen 10 Uhr !hoch @Anna #M365"
            aria-label="Aufgabe erfassen"
            aria-describedby="capture-preview"
            class="flex-1 min-w-0 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2 text-base sm:text-sm text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] disabled:opacity-60"
          />
        </div>

        <dl id="capture-preview" aria-live="polite" class="rounded border border-[color:var(--color-outline-variant)] px-3 py-2" data-testid="capture-preview">
          <Row label="Titel">{parsed.title.trim() || <span class={muted}>—</span>}</Row>
          <Row label="Ziel">{targetView}</Row>
          <Row label="Fällig">
            {dueLabel ? (
              <span>
                {dueLabel}
                {parsed.time && <span class={muted}> → Erinnerung um {parsed.time}</span>}
              </span>
            ) : (
              <span class={muted}>—</span>
            )}
          </Row>
          <Row label="Priorität">
            {parsed.priority ? (
              <span style={softPillStyle(TASK_PRIORITY_VAR[parsed.priority as TaskPriority])} class="inline-flex rounded-full px-2 py-0.5 text-xs font-medium">
                {TASK_PRIORITY_LABEL[parsed.priority as TaskPriority]}
              </span>
            ) : (
              <span class={muted}>—</span>
            )}
          </Row>
          <Row label="Zuständig">{assigneeView}</Row>
          {myDay && (
            <Row label="Mein Tag">
              <span data-testid="capture-myday">
                → wird für {myDay === today ? "heute" : formatDuePreview(myDay, undefined, today)} eingeplant
              </span>
            </Row>
          )}
        </dl>

        <div class={`flex flex-wrap items-center gap-x-4 gap-y-1 text-xs ${muted}`}>
          <span class="inline-flex items-center gap-1"><Kbd label="↵" /> anlegen</span>
          <span class="inline-flex items-center gap-1"><Kbd label="⇧↵" /> anlegen &amp; öffnen</span>
          <span class="inline-flex items-center gap-1"><Kbd label="⌘↵" /> anlegen &amp; nächste</span>
          <span class="ml-auto">ohne # → persönliche Standardliste</span>
        </div>

        {recent.length > 0 && (
          <section aria-label="Zuletzt erfasst">
            <h3 class={`text-micro font-semibold uppercase tracking-wider mb-1 ${muted}`}>Zuletzt erfasst</h3>
            <ul class="divide-y divide-[color:var(--color-outline-variant)] rounded border border-[color:var(--color-outline-variant)]">
              {recent.map((r) => (
                <li key={r.key}>
                  <button
                    type="button"
                    onClick={r.open}
                    class="w-full flex items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
                  >
                    <span class="min-w-0 flex-1 truncate">{r.title}</span>
                    <span class={`shrink-0 rounded bg-[color:var(--color-surface-container)] px-1.5 text-xs ${muted}`}>{r.where}</span>
                    {r.due && <span class={`shrink-0 text-xs ${muted}`}>{r.due}</span>}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </Modal>
  );
}
